import { useEffect } from 'react'
import { currentAdminCan } from '../data/currentAdminAccess'
import { orders, quoteProposals } from '../data/adminExtendedData'
import { directors, quoteRequests } from '../data/mockData'

const EXPORT_PATTERN = /(엑셀|Excel|CSV|내보내기|다운로드)/i
const APPROVE_PATTERN = /(승인|반려|거절|재심사|확정|이용 제한|요청 종료|블라인드|복구|완전 삭제|심사 완료|심사 반려|정산 처리|지급 처리|정지 해제)/
const EDIT_PATTERN = /(추가|등록|작성|수정|편집|삭제|초대|저장|생성|변경|활성화|비활성화|중지|발행|지정|새 운영 공지)/
const NAVIGATION_EXCEPTIONS = /(보기|미리보기|상세|이동|돌아가기|전체 실행 이력|사후관리|로그아웃|메뉴 닫기|대시보드)/

function requiredAction(button) {
  const label = button.textContent?.replace(/\s+/g, ' ').trim() || ''
  if (!label) return null
  if (EXPORT_PATTERN.test(label)) return 'export'
  if (APPROVE_PATTERN.test(label)) return 'approve'
  if (!NAVIGATION_EXCEPTIONS.test(label) && EDIT_PATTERN.test(label)) return 'edit'
  return null
}

function applyPermission(button, route) {
  const action = requiredAction(button)
  if (!action) return
  const allowed = currentAdminCan(route, action)
  button.disabled = !allowed
  button.setAttribute('aria-disabled', allowed ? 'false' : 'true')
  if (!allowed) {
    button.dataset.permissionBlocked = action
    button.title = action === 'export'
      ? '이 계정에는 다운로드 권한이 없습니다.'
      : action === 'approve'
        ? '이 계정에는 승인·처리 권한이 없습니다.'
        : '이 계정에는 등록·수정 권한이 없습니다.'
  } else if (button.dataset.permissionBlocked) {
    delete button.dataset.permissionBlocked
    if (button.title?.includes('권한이 없습니다')) button.removeAttribute('title')
  }
}

function selectedQuoteNumbers() {
  return new Set(orders.map((order) => order.quoteNo).filter(Boolean))
}

function selectedRequestNumbers() {
  return new Set(orders.map((order) => order.requestNo).filter(Boolean))
}

// 연결 주문이 존재한다는 것은 견적 선택과 해당 요청의 수신 단계가 이미 끝났다는 뜻입니다.
// 관리자 데모 원장도 앱이 읽기 전에 같은 상거래 단계로 정규화합니다.
const selectedAtSource = selectedQuoteNumbers()
quoteProposals.forEach((quote) => {
  if (selectedAtSource.has(quote.quoteNo) && quote.status === 'SUBMITTED') quote.status = 'SELECTED'
})

const selectedRequestsAtSource = selectedRequestNumbers()
quoteRequests.forEach((request) => {
  if (selectedRequestsAtSource.has(request.requestNo) && ['ANALYZED', 'DISTRIBUTED', 'RECEIVING_QUOTES'].includes(request.status)) request.status = 'SELECTED'
})

const commercialDirectorOrders = new Map()
orders.forEach((order) => {
  if (!order.directorId) return
  if (!commercialDirectorOrders.has(order.directorId)) commercialDirectorOrders.set(order.directorId, order)
})
directors.forEach((director) => {
  const linkedOrder = commercialDirectorOrders.get(director.id)
  if (!linkedOrder || !['UNDER_REVIEW', 'REVIEW_REQUESTED'].includes(director.status)) return
  director.status = 'APPROVED'
  if (!director.approvedAt) director.approvedAt = linkedOrder.paidAt || linkedOrder.createdAt
})

function normalizeSelectedQuoteState() {
  const selected = selectedQuoteNumbers()
  quoteProposals.forEach((quote) => {
    if (selected.has(quote.quoteNo) && quote.status === 'SUBMITTED') quote.status = 'SELECTED'
  })

  document.querySelectorAll('.admin-content table.data-table tbody tr').forEach((row) => {
    const cells = [...row.querySelectorAll('td')]
    const quoteNo = String(cells[0]?.querySelector('strong')?.textContent || cells[0]?.textContent || '').replace(/\s+/g, ' ').trim()
    if (!selected.has(quoteNo)) return
    const badge = row.querySelector('.status-badge')
    if (badge && String(badge.textContent || '').replace(/\s+/g, ' ').trim() === '제출') {
      badge.textContent = '견적 선택'
      badge.className = 'status-badge status-info'
      badge.title = '연결된 주문이 생성되어 선택 완료된 견적입니다.'
    }
  })
}

function normalizeCompletedProjectMilestone(route) {
  if (!/^\/projects\/[^/]+$/.test(route)) return
  const summaryItems = [...document.querySelectorAll('.admin-content .summary-grid > *')]
  const statusItem = summaryItems.find((item) => String(item.querySelector('span')?.textContent || '').replace(/\s+/g, ' ').trim() === '프로젝트 상태')
  const stageItem = summaryItems.find((item) => String(item.querySelector('span')?.textContent || '').replace(/\s+/g, ' ').trim() === '현재 단계')
  const status = String(statusItem?.querySelector('strong')?.textContent || statusItem?.lastElementChild?.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase()
  const stage = String(stageItem?.querySelector('strong')?.textContent || stageItem?.lastElementChild?.textContent || '').replace(/\s+/g, ' ').trim()
  if (status !== 'COMPLETED' && stage !== '진행 완료') return

  const track = document.querySelector('.admin-content .project-milestones')
  if (!track) return
  const rows = [...track.children]
  const last = rows.find((row) => String(row.querySelector('strong')?.textContent || '').replace(/\s+/g, ' ').trim() === '진행 완료')
  if (!last) return
  last.className = 'milestone-done'
  const labels = last.querySelectorAll('span')
  if (labels[0]) labels[0].textContent = '완료'
  if (labels[1]) labels[1].textContent = '완료'
}

export default function AdminActionPermissionEnhancer({ route }) {
  useEffect(() => {
    const root = document.querySelector('.admin-content') || document.body
    const refresh = () => {
      root.querySelectorAll('button').forEach((button) => applyPermission(button, route))
      normalizeSelectedQuoteState()
      normalizeCompletedProjectMilestone(route)
    }
    refresh()
    const observer = new MutationObserver(refresh)
    observer.observe(root, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
    }
  }, [route])
  return null
}
